/**
 * The prioritization memo — pure assembly (no IO).
 *
 * Roadmap AI-5; Brief K slice 3. When a ranking is published, the memo says why
 * the items above the line ranked where they did, what was traded off, and which
 * items narrowly missed the line, for people who were not in the room.
 *
 * Two kinds of words, never mixed:
 *   - Recorded: what people wrote down (rationale notes, override reasons, the
 *     method's own per-item notes) and, once linked, passages from the
 *     session's transcript. These are copied VERBATIM from the record, with who
 *     and when. The model never writes them, so it cannot put words in anyone's
 *     mouth.
 *   - Explained: the model's prose (headline, summary, trade-offs, a line per
 *     item). Labeled as AI on every surface. It may reference items only by the
 *     ids it was given; anything else it returns is dropped.
 *
 * The memo is written even when the AI assist is off: then it carries only the
 * recorded parts and says so. Where nothing was recorded for an item, it says
 * that too, instead of inventing a reason.
 *
 * The IO lives in reportGenerationService.ts (loadMemoSources /
 * writePrioritizationMemo); the single model call lives in
 * llmResultsExplainerService.ts (explainForMemo). Canon: Doc 21 §6.4.
 */

export const MEMO_TEMPLATE_SLUG = 'prioritization_memo';
export const MEMO_KIND = 'prioritization_memo';
/** How many items just below the line the memo covers. */
export const NEAR_MISS_COUNT = 3;
/** With no cut line, how many of the top items the memo covers. */
export const NO_CUT_LINE_COVERAGE = 10;
/** Characters of transcript the model sees. */
export const TRANSCRIPT_CHAR_BUDGET = 12_000;

// ── Inputs: what the record holds ────────────────────────────────────────────

export interface MemoSourceRanking {
  itemId: string;
  rank: number;
  score: number | null;
}

export interface MemoSourceAnnotation {
  itemId: string;
  authorName: string | null;
  text: string;
  at: string | null;
  /** manual | chat_excerpt | transcript_excerpt | recording */
  source: string;
  transcriptTimestamp: string | null;
}

export interface MemoSourceOverride {
  itemId: string;
  byName: string | null;
  reason: string;
  fromRank: number | null;
  toRank: number | null;
  at: string | null;
}

export interface MemoSourceDocument {
  title: string;
  type: string;
  hasFile: boolean;
  url: string | null;
}

export interface MemoSources {
  session: {
    id: string;
    name: string;
    workspaceId: string | null;
    cycleName: string | null;
    trackName: string | null;
    toolName: string | null;
    publishedAt: string | null;
    participantCount: number;
  };
  criteria: Array<{ id: string; label: string; weight: number | null }>;
  /** Sorted by rank. */
  ranking: MemoSourceRanking[];
  cutLine: number | null;
  itemTitles: Record<string, string>;
  /** The method's own per-item notes (ranking_output.itemNotes). */
  itemNotes: Record<string, string>;
  annotations: MemoSourceAnnotation[];
  overrides: MemoSourceOverride[];
  documents: MemoSourceDocument[];
  /** Pasted transcripts and the in-browser recording, joined. May be empty. */
  transcriptText: string;
}

// ── Output: the memo as stored in report_artifacts.content ──────────────────

export type MemoReasonKind = 'recorded' | 'from_the_room' | 'method_note';

export interface MemoReason {
  kind: MemoReasonKind;
  who: string | null;
  text: string;
  at: string | null;
  /** Position in the recording or transcript, when the passage came from one. */
  timestamp: string | null;
}

export interface MemoItem {
  itemId: string;
  rank: number;
  title: string;
  score: number | null;
  /** Other ranks that carry the same score. */
  tiedWithRanks: number[];
  reasons: MemoReason[];
  /** The model's line for this item, or null. */
  explained: string | null;
}

export interface MemoOverride {
  itemId: string;
  title: string;
  by: string | null;
  reason: string;
  fromRank: number | null;
  toRank: number | null;
  at: string | null;
}

export interface PrioritizationMemoContent {
  kind: typeof MEMO_KIND;
  version: 1;
  session: MemoSources['session'];
  criteria: MemoSources['criteria'];
  headline: string;
  /** The model's summary, or null when the AI was not used. */
  summary: string | null;
  /** The model's account of what was traded off, or null. */
  tradeOffs: string | null;
  cutLine: number | null;
  aboveLine: MemoItem[];
  nearMisses: MemoItem[];
  /** Items below the line beyond the near misses. */
  belowLineCount: number;
  /** Items ranked but not covered when there is no cut line. */
  notCoveredCount: number;
  overrides: MemoOverride[];
  /** Covered items with nothing recorded about them. */
  itemsWithoutReasons: string[];
  sources: {
    documents: MemoSourceDocument[];
    transcriptChars: number;
    recordedReasonCount: number;
    aiWritten: boolean;
    /** Why the AI did not write, when it did not. Plain words. */
    aiNote: string | null;
  };
}

/** The model's answer, after validation. */
export interface MemoExplanation {
  headline: string | null;
  summary: string | null;
  tradeOffs: string | null;
  items: Record<string, string>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** "risk-of-doing-nothing" → "Risk of doing nothing". Names that are already names pass through. */
export function humanizeCriterionId(id: string): string {
  const trimmed = id.trim();
  if (!trimmed) return id;
  if (/\s/.test(trimmed) || /[A-Z]/.test(trimmed.slice(1))) return trimmed;
  const words = trimmed.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function reasonKind(source: string): MemoReasonKind {
  return source === 'transcript_excerpt' || source === 'recording' ? 'from_the_room' : 'recorded';
}

function clip(text: string, max: number): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

/** Which items the memo covers: above the line, and the first few just below it. */
export function splitAtCutLine(
  ranking: MemoSourceRanking[],
  cutLine: number | null,
): { above: MemoSourceRanking[]; nearMisses: MemoSourceRanking[]; belowRest: number; notCovered: number } {
  const sorted = [...ranking].sort((a, b) => a.rank - b.rank);
  if (cutLine == null || cutLine <= 0 || cutLine >= sorted.length) {
    const above = sorted.slice(0, cutLine && cutLine > 0 ? cutLine : NO_CUT_LINE_COVERAGE);
    return { above, nearMisses: [], belowRest: 0, notCovered: Math.max(0, sorted.length - above.length) };
  }
  const above = sorted.slice(0, cutLine);
  const below = sorted.slice(cutLine);
  const nearMisses = below.slice(0, NEAR_MISS_COUNT);
  return { above, nearMisses, belowRest: below.length - nearMisses.length, notCovered: 0 };
}

function reasonsFor(itemId: string, sources: MemoSources): MemoReason[] {
  const out: MemoReason[] = [];
  for (const a of sources.annotations) {
    if (a.itemId !== itemId || !a.text.trim()) continue;
    out.push({
      kind: reasonKind(a.source),
      who: a.authorName,
      text: a.text.trim(),
      at: a.at,
      timestamp: a.transcriptTimestamp,
    });
  }
  const note = sources.itemNotes[itemId];
  if (note && note.trim()) {
    out.push({ kind: 'method_note', who: null, text: note.trim(), at: null, timestamp: null });
  }
  return out;
}

function tiesFor(entry: MemoSourceRanking, ranking: MemoSourceRanking[]): number[] {
  if (entry.score == null) return [];
  return ranking
    .filter((r) => r.itemId !== entry.itemId && r.score != null && Math.abs(r.score - (entry.score as number)) < 1e-9)
    .map((r) => r.rank)
    .sort((a, b) => a - b);
}

// ── The prompt and its answer ────────────────────────────────────────────────

/** The prompt for the one model call. Recorded reasons are context only; the reader sees them verbatim. */
export function buildMemoPrompt(sources: MemoSources): string {
  const { above, nearMisses } = splitAtCutLine(sources.ranking, sources.cutLine);
  const title = (id: string) => sources.itemTitles[id] ?? 'Untitled item';
  const line = (r: MemoSourceRanking) => {
    const reasons = reasonsFor(r.itemId, sources);
    const recorded = reasons.length
      ? ` | recorded: ${reasons.map((x) => `"${clip(x.text, 220)}"`).join('; ')}`
      : ' | recorded: nothing';
    const score = r.score != null ? ` | score ${Number(r.score.toFixed(2))}` : '';
    const ties = tiesFor(r, sources.ranking);
    const tie = ties.length ? ` | same score as rank ${ties.join(', ')}` : '';
    return `- id=${r.itemId} | #${r.rank} ${title(r.itemId)}${score}${tie}${recorded}`;
  };

  const criteria = sources.criteria.length
    ? sources.criteria.map((c) => (c.weight != null ? `${c.label} (weight ${c.weight})` : c.label)).join(', ')
    : 'none recorded';
  const overrides = sources.overrides.length
    ? sources.overrides
        .map((o) => `- ${title(o.itemId)} moved${o.fromRank != null ? ` from #${o.fromRank}` : ''}${o.toRank != null ? ` to #${o.toRank}` : ''}: "${clip(o.reason, 220)}"`)
        .join('\n')
    : 'none';
  const transcript = sources.transcriptText.trim()
    ? clip(sources.transcriptText, TRANSCRIPT_CHAR_BUDGET)
    : '';

  const covered = [...above, ...nearMisses];
  const withReasons = covered.filter((r) => reasonsFor(r.itemId, sources).length > 0);
  const withoutReasons = covered.filter((r) => reasonsFor(r.itemId, sources).length === 0);
  const nothingBelow = nearMisses.length === 0 && covered.length === sources.ranking.length;
  const recordedFacts = `Items covered by this memo: ${covered.length}. Items with a recorded reason: ${withReasons.length}${
    withoutReasons.length ? `. Nothing was recorded for: ${withoutReasons.map((r) => title(r.itemId)).join('; ')}` : ''
  }.`;

  return `You are writing a short prioritization memo for people who were NOT in the session. Plain words. No jargon. No sales tone.

Session: ${sources.session.name}
Method: ${sources.session.toolName ?? 'not recorded'}
Participants: ${sources.session.participantCount}
Criteria: ${criteria}
Cut line: ${sources.cutLine != null ? `after rank #${sources.cutLine}` : 'none set'}
${recordedFacts}

Above the line:
${above.map(line).join('\n') || '- none'}

Just below the line:
${nearMisses.map(line).join('\n') || '- none'}

Rank overrides, with the reason given:
${overrides}
${transcript ? `\nWhat was said in the session (transcript, may be partial):\n"""\n${transcript}\n"""\n` : ''}
Rules:
- The facts you have are the scores, ties, criteria and weights, the cut line, the overrides, the recorded words, and the transcript. Say nothing that is not in them.
- When you say what was or was not recorded, use the counts above exactly.
- Do not say why the group valued an item, how important it is, or what it is for, unless a recorded reason or the transcript says so.
- The reader sees every recorded reason word for word under its item. Do not repeat, paraphrase or summarize them.
- Never attribute words to a named person. Refer to the transcript only as "in the discussion".
- Mention ties when items share a score.

Return JSON only:
{
  "headline": "one sentence: what was decided",
  "summary": "two to four sentences for someone who was not there: what made the cut, how close it was, and how much of it has a recorded reason",
  "tradeOffs": ${nothingBelow ? 'null (nothing was left below the line, so nothing was traded off)' : '"two to four sentences: which items were set aside, how far below the line they landed, and any that only just missed"'}
}`;
}

/** Validate the model's answer. Unknown ids and non-strings are dropped, lengths capped. */
export function parseMemoExplanation(raw: string | null, allowedItemIds: Iterable<string>): MemoExplanation | null {
  if (!raw) return null;
  const match = raw.match(/\{[\s\S]*\}/);
  if (!match) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(match[0]);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const p = parsed as Record<string, unknown>;
  const str = (v: unknown, max: number) => (typeof v === 'string' && v.trim() ? clip(v, max) : null);
  const allowed = new Set(allowedItemIds);
  const items: Record<string, string> = {};
  if (Array.isArray(p.items)) {
    for (const entry of p.items) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as Record<string, unknown>;
      const id = typeof e.id === 'string' ? e.id.trim() : '';
      const why = str(e.why, 600);
      if (id && why && allowed.has(id) && !items[id]) items[id] = why;
    }
  }
  const out: MemoExplanation = {
    headline: str(p.headline, 240),
    summary: str(p.summary, 1200),
    tradeOffs: str(p.tradeOffs, 1200),
    items,
  };
  if (!out.headline && !out.summary && !out.tradeOffs && Object.keys(items).length === 0) return null;
  return out;
}

// ── Assembly ─────────────────────────────────────────────────────────────────

export function assembleMemo(
  sources: MemoSources,
  explanation: MemoExplanation | null,
  aiNote: string | null,
): PrioritizationMemoContent {
  const { above, nearMisses, belowRest, notCovered } = splitAtCutLine(sources.ranking, sources.cutLine);
  const title = (id: string) => sources.itemTitles[id] ?? 'Untitled item';
  const toItem = (r: MemoSourceRanking): MemoItem => ({
    itemId: r.itemId,
    rank: r.rank,
    title: title(r.itemId),
    score: r.score,
    tiedWithRanks: tiesFor(r, sources.ranking),
    reasons: reasonsFor(r.itemId, sources),
    // No model prose per item. Where words were recorded they are quoted and speak for
    // themselves (a model line beside them only paraphrased them, checked on staging
    // 2026-10-14); where nothing was recorded the memo says so. A model cannot supply
    // an item's reason. (Memos written before this read `explained`; it stays in the shape.)
    explained: null,
  });

  const aboveItems = above.map(toItem);
  const nearItems = nearMisses.map(toItem);
  const covered = [...aboveItems, ...nearItems];

  const fallbackHeadline =
    sources.cutLine != null && sources.cutLine > 0
      ? `${sources.session.name}: ${aboveItems.length} item${aboveItems.length === 1 ? '' : 's'} above the line`
      : `${sources.session.name}: the ranking as published`;

  return {
    kind: MEMO_KIND,
    version: 1,
    session: sources.session,
    criteria: sources.criteria,
    headline: explanation?.headline ?? fallbackHeadline,
    summary: explanation?.summary ?? null,
    // Nothing below the line means nothing was traded off; the section is left out, not filled.
    tradeOffs: nearItems.length + belowRest + notCovered > 0 ? explanation?.tradeOffs ?? null : null,
    cutLine: sources.cutLine,
    aboveLine: aboveItems,
    nearMisses: nearItems,
    belowLineCount: belowRest,
    notCoveredCount: notCovered,
    overrides: sources.overrides.map((o) => ({
      itemId: o.itemId,
      title: title(o.itemId),
      by: o.byName,
      reason: o.reason,
      fromRank: o.fromRank,
      toRank: o.toRank,
      at: o.at,
    })),
    itemsWithoutReasons: covered.filter((i) => i.reasons.length === 0).map((i) => i.title),
    sources: {
      documents: sources.documents,
      transcriptChars: sources.transcriptText.trim().length,
      recordedReasonCount: covered.reduce((n, i) => n + i.reasons.length, 0),
      aiWritten: explanation != null,
      aiNote: explanation ? null : aiNote,
    },
  };
}

/** True when a report artifact's content is a memo. */
export function isPrioritizationMemo(content: unknown): content is PrioritizationMemoContent {
  return !!content && typeof content === 'object' && (content as { kind?: unknown }).kind === MEMO_KIND;
}

/** The memo's page in the app. */
export function memoHref(sessionId: string, artifactId: string): string {
  return `/prioritization/sessions/${sessionId}/reports/${artifactId}`;
}
